import { calculateDynamicGridLines } from "../tableManipulation/addRowColumn";
import { findWordsInShape } from "../utils/geometryCalculations";
import { useEffect, useRef } from "react";

/**
 * Hook for PDF and Table initialization logic
 * Handles PDF.js loading, table selection, PDF rendering, and drawing preview updates
 */
const usePdfTableInitialization = ({
  // PDF state
  pdfBlob,
  pdfDoc,
  totalPages,
  currentPage,
  pdfPanelExpanded,
  selectedCell,

  // UI State
  isInitialPdfRenderComplete,

  // Table state
  tables,
  selectedTable,
  tableData,

  // Drawing state
  drawingShape,
  showDrawingConfirmation,
  pdfPageDimensions,

  // Grid line state
  isDraggingGridLine,
  isGridLineInPreview,
  limitColumnAdditionsToCurrentPage,

  // State setters
  pdfState,
  tableState,
  drawingState,
  gridLineState,

  // Functions
  loadPdf,
  renderPage,
  initializeTableIdsInternal,
  highlightWords,
  preRenderAllPages,
}) => {
  // Load PDF.js library
  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
    script.onload = () => {
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
      }
    };
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // Load PDF document
  useEffect(() => {
    if (pdfBlob && window.pdfjsLib) {
      // Add a small delay to ensure the component is fully mounted
      const timer = setTimeout(() => {
        loadPdf(pdfBlob);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [pdfBlob, loadPdf]);

  // Auto-select first non-separator table
  useEffect(() => {
    if (tables.length > 0 && !selectedTable) {
      const firstNonSeparator = tables.find((t) => !t.isSeparator);
      if (firstNonSeparator) {
        tableState.setSelectedTable(firstNonSeparator);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tables, selectedTable, tableState.setSelectedTable]);

  // Render PDF after table is selected
  useEffect(() => {
    // Only render PDF after table is selected and page is determined
    if (selectedTable) {
      // Add a small delay to ensure canvas is properly mounted and sized
      const timer = setTimeout(() => {
        renderPage(null, selectedCell);
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [renderPage, selectedTable, selectedCell]);

  // Re-render PDF when panel becomes visible
  useEffect(() => {
    if (pdfPanelExpanded && pdfDoc && selectedTable) {
      // Small delay to ensure the canvas is properly mounted
      const timer = setTimeout(() => {
        renderPage(null, selectedCell);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [pdfPanelExpanded, pdfDoc, renderPage, selectedTable, selectedCell]);

  // Re-render PDF when page changes
  useEffect(() => {
    if (pdfDoc && selectedTable) {
      // Add a small delay to ensure smooth page transitions
      const timer = setTimeout(() => {
        renderPage(null, selectedCell);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [currentPage, pdfDoc, selectedTable, renderPage, selectedCell]);

  // Initial PDF rendering when document is first loaded
  useEffect(() => {
    if (pdfDoc && totalPages > 0) {
      // Add a delay to ensure canvas is ready
      const timer = setTimeout(() => {
        renderPage(null, selectedCell);
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [pdfDoc, totalPages, renderPage, selectedCell]);

  // Auto-select first table when tables are loaded
  useEffect(() => {
    if (tables.length > 0 && !selectedTable) {
      const firstTable = tables[0];
      tableState.setSelectedTable(firstTable);

      // Navigate to the table's page (same logic as handleTableChange)
      const cellKeys = Object.keys(firstTable).filter((key) =>
        key.match(/^\d+_\d+$/)
      );
      if (cellKeys.length > 0) {
        const firstCellKey = cellKeys[0];
        const cellLocationData = firstTable[firstCellKey];
        if (
          cellLocationData &&
          Array.isArray(cellLocationData) &&
          cellLocationData[0]
        ) {
          const tablePage = cellLocationData[0].page || 1;
          pdfState.setCurrentPage(tablePage);
        }
      }

      const initialTableData = {
        headers: [...(firstTable.headers || [])],
        rows: firstTable.rows ? firstTable.rows.map((row) => [...row]) : [],
      };

      initializeTableIdsInternal(initialTableData);
      tableState.setTableData(initialTableData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tables,
    selectedTable,
    initializeTableIdsInternal,
    tableState.setSelectedTable,
    tableState.setTableData,
    pdfState.setCurrentPage,
  ]);

  // On initial load, automatically select and highlight the first cell.
  // This ensures a cell is active by default and also corrects a rendering orientation issue.
  const initialCellSelectionApplied = useRef(false);
  useEffect(() => {
    // Ensure this runs only once after the PDF render is complete and data is available.
    if (
      isInitialPdfRenderComplete &&
      tables.length > 0 &&
      selectedTable &&
      !initialCellSelectionApplied.current
    ) {
      initialCellSelectionApplied.current = true; // Prevents this effect from running again.
      const firstTable = tables.find((t) => !t.isSeparator) || tables[0];
      if (!firstTable) return;

      const firstCellKey = "0_0";
      const cellLocationData = firstTable[firstCellKey];

      if (
        cellLocationData &&
        Array.isArray(cellLocationData) &&
        cellLocationData[0]
      ) {
        const cellInfo = {
          row: 0,
          col: 0,
          text: firstTable.headers?.[0] || "",
          locationData: cellLocationData,
          page: cellLocationData[0].page || 1,
          words: cellLocationData[0].words || [],
        };

        tableState.setSelectedCell(cellInfo);

        // Short delay to allow state update before triggering the highlight
        setTimeout(() => {
          if (highlightWords) {
            highlightWords(cellInfo);
          }
        }, 50);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isInitialPdfRenderComplete,
    tables,
    selectedTable,
    tableState.setSelectedCell,
    highlightWords,
  ]);

  // Update preview words when drawing shape changes
  useEffect(() => {
    if (drawingShape && !showDrawingConfirmation) {
      const wordsInShape = findWordsInShape(
        drawingShape,
        selectedTable,
        currentPage,
        pdfPageDimensions
      );
      drawingState.setPreviewWords(wordsInShape);
    } else {
      drawingState.setPreviewWords([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    drawingShape,
    selectedTable,
    currentPage,
    pdfPageDimensions,
    showDrawingConfirmation,
    drawingState.setPreviewWords,
  ]);

  // Update dynamic grid lines when content changes
  // BUT NOT when user is actively dragging, previewing, or has pending grid line operations
  useEffect(() => {
    if (
      selectedTable &&
      tableData &&
      !isDraggingGridLine &&
      !isGridLineInPreview
    ) {
      // Don't recalculate if we have pending grid line changes (user might be in confirmation flow)
      const hasPendingGridLineChanges =
        gridLineState.pendingGridLineChanges !== null;
      const hasOriginalGridLines = gridLineState.originalGridLines !== null;

      // Only recalculate if we don't have any grid lines at all, or if the content has actually changed
      const hasNoGridLines =
        !gridLineState.dynamicGridLines ||
        !gridLineState.dynamicGridLines[currentPage];

      if (
        !hasPendingGridLineChanges &&
        !hasOriginalGridLines &&
        hasNoGridLines
      ) {
        const newGridLines = calculateDynamicGridLines(
          selectedTable,
          tableData,
          currentPage,
          pdfPageDimensions,
          limitColumnAdditionsToCurrentPage
        );
        gridLineState.setDynamicGridLines(newGridLines);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTable,
    tableData,
    currentPage,
    pdfPageDimensions,
    isDraggingGridLine,
    isGridLineInPreview,
    limitColumnAdditionsToCurrentPage,
    gridLineState.pendingGridLineChanges,
    gridLineState.originalGridLines,
    gridLineState.dynamicGridLines,
    gridLineState.setDynamicGridLines,
  ]);

  // Start pre-rendering after initial render is complete
  useEffect(() => {
    if (isInitialPdfRenderComplete && selectedTable && preRenderAllPages) {
      // Small delay to ensure the initial page is fully rendered
      const timer = setTimeout(() => {
        preRenderAllPages(currentPage);
      }, 1000); // 1 second delay to let initial render complete

      return () => clearTimeout(timer);
    }
  }, [
    isInitialPdfRenderComplete,
    selectedTable,
    currentPage,
    preRenderAllPages,
  ]);
};

export default usePdfTableInitialization;
